import React, { useEffect, useState } from "react";
import { IndianRupee, X } from "lucide-react";
import Button, { ButtonType } from "../Assets/Button";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";

interface DeductCommissionModalProps {
  isOpen: boolean;
  customerName: string;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: (amount: number) => void | Promise<void>;
}

const DeductCommissionModal: React.FC<DeductCommissionModalProps> = ({
  isOpen,
  customerName,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const [amount, setAmount] = useState("");
  const handleWheelPrevent = usePreventWheelChange();

  useEffect(() => {
    if (isOpen) setAmount("");
  }, [isOpen]);

  if (!isOpen) return null;

  const parsedAmount = Number(amount);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Deduct from Commission
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close"
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <p className="font-medium text-gray-900">{customerName}</p>
          <div>
            <label
              htmlFor="commission-deduction-amount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Amount to deduct
            </label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="commission-deduction-amount"
                type="number"
                min="0.01"
                step="any"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                onWheel={handleWheelPrevent}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter amount"
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type={ButtonType.Cancel}
              onClick={onClose}
              disabled={isLoading}
              className="text-sm"
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.Extra}
              onClick={() => onConfirm(parsedAmount)}
              disabled={
                isLoading ||
                !Number.isFinite(parsedAmount) ||
                parsedAmount <= 0
              }
              className="text-sm"
            >
              {isLoading ? "Processing..." : "Confirm Deduction"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeductCommissionModal;
